
import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { LayoutDashboard, Truck, Settings, LogOut, Lock, BookOpen, Mail, MessageSquare, Server, MailOpen } from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

const AdminLayout: React.FC = () => {
  const { isAuthenticated, login, logout, settings } = useData();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (login(password)) {
      setError('');
    } else {
      setError('Invalid password');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-sm shadow-2xl w-full max-md:max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              {settings.logoUrl ? (
                <img
                  src={settings.logoUrl}
                  alt={settings.siteName}
                  className="h-16 w-auto object-contain mb-2"
                />
              ) : (
                <div className="bg-automax-red p-3 rounded-full">
                  <Lock className="text-white" size={24} />
                </div>
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Portal Access</h2>
            <p className="text-gray-500">Enter your credentials to access the management panel.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 p-3 rounded-sm focus:border-automax-red focus:outline-none text-gray-900 placeholder-gray-500"
                placeholder="Enter password"
              />
            </div>
            {error && <p className="text-red-600 text-sm">{error}</p>}
            <button type="submit" className="w-full bg-neutral-900 text-white font-bold py-3 rounded-sm hover:bg-automax-red transition-colors">
              Login
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-automax-red">← Back to Website</Link>
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: <LayoutDashboard size={20} /> },
    { name: 'Inventory', path: '/admin/inventory', icon: <Truck size={20} /> },
    { name: 'Inquiries', path: '/admin/inquiries', icon: <MessageSquare size={20} /> },
    { name: 'Blog Posts', path: '/admin/blog', icon: <BookOpen size={20} /> },
    { name: 'Email Logs', path: '/admin/email-logs', icon: <MailOpen size={20} /> },
    { name: 'Email Marketing', path: '/admin/email-marketing', icon: <Mail size={20} /> },
    { name: 'Email Config', path: '/admin/email-config', icon: <Server size={20} /> },
    { name: 'Site Settings', path: '/admin/settings', icon: <Settings size={20} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex font-sans">
      {/* Sidebar */}
      <aside className="w-64 bg-neutral-900 text-white hidden md:flex flex-col flex-shrink-0">
        <div className="p-6 border-b border-gray-800">
          {settings.logoUrl ? (
            <img
              src={settings.logoUrl}
              alt={settings.siteName}
              className="h-8 w-auto object-contain brightness-0 invert"
            />
          ) : (
            <span className="font-display text-3xl font-bold tracking-wider text-white">
              AUTO<span className="text-automax-red">MAX</span>
            </span>
          )}
          <p className="text-xs text-gray-500 mt-1">Management Panel</p>
        </div>
        <nav className="flex-grow p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-3 px-4 py-3 rounded-sm transition-colors ${location.pathname === item.path
                ? 'bg-automax-red text-white shadow-lg shadow-red-900/20'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
            >
              {item.icon}
              <span className="font-medium">{item.name}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="flex items-center space-x-3 px-4 py-3 w-full text-left text-gray-400 hover:text-white hover:bg-gray-800 rounded-sm transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header (visible only on small screens) */}
      <div className="md:hidden fixed top-0 w-full bg-neutral-900 text-white z-50 p-4 flex justify-between items-center shadow-md">
        {settings.logoUrl ? (
          <img
            src={settings.logoUrl}
            alt={settings.siteName}
            className="h-8 w-auto object-contain brightness-0 invert"
          />
        ) : (
          <span className="font-display text-2xl font-bold uppercase tracking-widest">AUTOMAX</span>
        )}
        <button onClick={() => { logout(); navigate('/'); }}><LogOut size={20} /></button>
      </div>

      {/* Main Content */}
      <main className="flex-grow p-4 md:p-8 mt-16 md:mt-0 overflow-y-auto h-screen bg-gray-50 text-gray-900">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
